import React from 'react';
import { BRAND } from '../brand';

/**
 * Marca de Open Dahu.
 *
 * El icono es el logotipo original —un íbice cuyo cuerno se enrosca hasta
 * formar el signo de interrogación— recortado a la baldosa con las esquinas
 * transparentes, en `public/logo.png`. El original sin recortar se conserva en
 * `brand/logo-original.jpeg`, fuera de lo que se sirve.
 *
 * `DahuGlyph` es la versión en trazo para los sitios donde el icono a color no
 * encaja: marcas de agua, impresión a una tinta o un tamaño tan pequeño que el
 * detalle del dibujo se pierde. Usa `currentColor`, así que hereda el color del
 * contexto.
 */

export const AppIcon: React.FC<{ size?: number; className?: string }> = ({
  size = 40,
  className = '',
}) => (
  <img
    src="/logo.png"
    width={size}
    height={size}
    alt=""
    aria-hidden="true"
    className={`shrink-0 select-none ${className}`}
    style={{ width: size, height: size }}
    draggable={false}
  />
);

/** Icono + nombre. El bloque que va en la cabecera. */
export const Logo: React.FC<{
  size?: number;
  showTagline?: boolean;
  className?: string;
  /** Clases para el nombre, p. ej. para esconderlo en la barra del teléfono. */
  wordmarkClassName?: string;
}> = ({ size = 40, showTagline = false, className = '', wordmarkClassName = 'flex' }) => (
  <span className={`inline-flex items-center gap-3 ${className}`}>
    <AppIcon size={size} />
    <span className={`flex-col leading-none text-left ${wordmarkClassName}`}>
      <span
        className="font-display font-bold tracking-tight text-ink-50"
        style={{ fontSize: size * 0.52 }}
      >
        {BRAND.name}
      </span>
      {showTagline && (
        <span className="text-ink-400 mt-1" style={{ fontSize: size * 0.26 }}>
          {BRAND.tagline}
        </span>
      )}
    </span>
  </span>
);

/**
 * Versión a una tinta del mismo motivo, para contextos donde el icono a color
 * no sirve. Silueta simplificada: el cuerno describe el arco del signo, el
 * cuello baja y se convierte en el asta, y el punto cierra abajo.
 */
export const DahuGlyph: React.FC<{ className?: string; title?: string }> = ({
  className,
  title,
}) => (
  <svg
    viewBox="0 0 100 100"
    className={className}
    role={title ? 'img' : 'presentation'}
    aria-label={title}
    aria-hidden={title ? undefined : true}
  >
    {/* Cuerno: el trazo que sostiene la legibilidad a tamaño pequeño. */}
    <path
      d="M 23 47 C 23 23, 41 14, 55 20 C 66 25, 68 37, 60 43"
      fill="none"
      stroke="currentColor"
      strokeWidth="13"
      strokeLinecap="round"
    />
    {/* Oreja. */}
    <path fill="currentColor" d="M 53 31 L 41 25 C 44 19, 53 20, 56 27 Z" />
    {/* Cabeza y hocico. */}
    <path
      fill="currentColor"
      d="M 54 30 C 64 28, 74 33, 82 40 C 88 45, 92 50, 89 52 C 87 53, 84 52, 82 51
         L 78 55 C 73 59, 64 59, 59 55 C 54 51, 51 44, 52 35 Z"
    />
    {/* Perilla: lo que hace que se lea como cabra. */}
    <path fill="currentColor" d="M 72 58 C 76 64, 75 71, 69 71 C 69 66, 69 61, 70 57 Z" />
    {/* Cuello convertido en asta. */}
    <path fill="currentColor" d="M 59 57 C 59 67, 56 73, 55 81 L 67 81 C 68 71, 70 64, 71 55 Z" />
    {/* Punto. */}
    <circle fill="currentColor" cx="60" cy="91" r="7" />
  </svg>
);
